import { useCallback, useEffect, useRef } from "react";

/**
 * Utility function that allows us to define event callbacks that
 * use the most recent values of its dependencies without triggering
 * a re-render when its dependencies change.
 * This is safe as long as we don't use such functions for actually
 * rendering the component.
 */
export function useEvent<TArgs extends unknown[], TRet>(
  fn: (...args: TArgs) => TRet
): (...args: TArgs) => TRet {
  const ref = useRef<(...args: TArgs) => TRet>(fn);

  useEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback<(...args: TArgs) => TRet>(
    (...args: TArgs) => ref.current(...args),
    []
  );
}
